import React, { useState } from "react";

import { url } from "@notabene/validation";
import styled from "styled-components";

import { Anchor, ClickableText } from "~/components/Button";
import CollapsibleDiv from "~/components/CollapsibleDiv";
import * as DetailList from "~/components/DetailList";
import { Box, Content, Padding, Separator } from "~/components/Elements";
import * as Grid from "~/components/Grid";
import ExternalIcon from "~/components/icons/External";
import Skeleton from "~/components/Skeleton";
import SkeletonDetailList from "~/components/SkeletonDetailList";
import Spacer, { Axis } from "~/components/Spacer";
import { Paragraph } from "~/components/Text";
import VASPAddress from "~/templates/companies/components/VASPAddress";
import VASPCertificates from "~/templates/companies/components/VASPCertificates";
import VASPHqAddress from "~/templates/companies/components/VASPHqAddress";
import VASPIncorporation from "~/templates/companies/components/VASPIncorporation";
import VASPStatusIcon from "~/templates/companies/components/VASPStatusIcon";
import { VASP } from "~/types/vaspid";

const EMPTY = "-";

const Container = styled.div`
  width: 100%;
`;

export default function VASPDescription({ vasp }: { vasp?: VASP | null }) {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const toggleCollapsed = () => setIsCollapsed(!isCollapsed);

  return (
    <Box as={Container}>
      <Padding sized="medium">
        <Grid.Columns multiline>
          <Grid.Column mobile={12} tablet={12} desktop={9}>
            <Content>
              {vasp ? (
                <Paragraph className="localize-ignore">{vasp.description || EMPTY}</Paragraph>
              ) : (
                <Paragraph>
                  <Skeleton count={5} />
                </Paragraph>
              )}
            </Content>
          </Grid.Column>
          <Grid.Column mobile={12} tablet={12} desktop={3}>
            {vasp ? (
              <DetailList.Content>
                {[
                  { label: "Website", value: url.isValidSync(vasp.website) ? vasp.website : undefined, status: null },
                ].map(({ label, value, status }) => (
                  <DetailList.Item key={label} mobile={12} tablet={6} desktop={12}>
                    <DetailList.Term>{label}</DetailList.Term>
                    <DetailList.Description className="localize-ignore">
                      {value ? (
                        <Anchor href={value} target="_blank" rel="noreferrer" weight="normal" color="black">
                          {value} <ExternalIcon /> <VASPStatusIcon status={status} />
                        </Anchor>
                      ) : (
                        EMPTY
                      )}
                    </DetailList.Description>
                  </DetailList.Item>
                ))}
              </DetailList.Content>
            ) : (
              <SkeletonDetailList count={3} mobile={12} tablet={6} desktop={12} />
            )}
          </Grid.Column>
        </Grid.Columns>
        <CollapsibleDiv collapsed={isCollapsed}>
          <Separator />
          <VASPIncorporation vasp={vasp} />
          <Separator />
          <VASPAddress vasp={vasp} />
          <Separator />
          <VASPHqAddress vasp={vasp} />
          <Separator />
          <VASPCertificates vasp={vasp} />
          <Spacer axis={Axis.VERTICAL} size={50} />
        </CollapsibleDiv>
        <ClickableText onClick={toggleCollapsed} weight="bolder" color="grey-dark">
          View {isCollapsed ? "more" : "less"} company information
        </ClickableText>
      </Padding>
    </Box>
  );
}
